// 資訊頁的「遊戲專屬」段落 — 逐節點對照 Unity `Slot026_RedRidingHood/Prefab/GameRuleInfo.prefab`。
//
// 該 prefab 沒有掛任何 Script，是純靜態版面：根節點 VerticalLayoutGroup（padding 上下 40、spacing 40、
// UpperCenter）＋ ContentSizeFitter，底下依序 24 個子節點。本檔即照這 24 個節點的順序與序列化尺寸重建。
//
// 內文寬度刻意逐段不同（Unity 各節點的 sizeDelta.x，部分寬於 1000 欄寬——ChildControlWidth=0 故不被壓縮），
// 直接照抄可讓換行位置與 Unity 一致。所有字串走 Localization key（**不可**用 prefab 的 m_text：
// `CASCADING REELS` / `BUY FREE SPINS` 兩個節點的序列化文字都殘留成 "BUY FEATURE"，執行期才被 key 覆蓋）。
//
// 版面數值以 `scripts/dump-unity-prefab-tree.mjs` 直接讀 prefab 取得，非目測。

import { t } from '../../../../framework/i18n/i18n'
import { getUiTexture } from '../../../assets/UiTextures'
import { getScatterWolfTexture, getSymbolTexture, getWildVariantTexture } from '../../../assets/SymbolTextures'
import {
  bodyBlock,
  captionedImageBlock,
  GAME_TITLE_GRADIENT,
  groupBlock,
  imageBlock,
  imageRowBlock,
  levelBarBlock,
  titleBlock,
  type InfoBlock,
} from './infoBlocks'
import { payTableBlock } from './PayTableGrid'

/** 一般符號格高（214×134）；`_1x4` 變體即 4 倍高。 */
const SYMBOL_W = 214
const SYMBOL_ROW_H = 192
const CHARACTER_H = 536

/** `WildSymbols` 群組內的 spacing（巢狀 VerticalLayoutGroup）。 */
const WILD_GROUP_SPACING = 20
/** `Symbols`／`Symbols_1` 兩排並列圖的 HorizontalLayoutGroup spacing（682 = 3×214 + 2×20）。 */
const SYMBOL_ROW_SPACING = 20
/** `CharactersWildImage` 的 HorizontalLayoutGroup spacing（1000 容器內 3×214 + 2×50 置中）。 */
const CHARACTER_ROW_SPACING = 50

/** `FreeSpinIngo`（884×100 說明用等級條）逐節點座標，相對容器中心、y 向上。 */
function freeSpinInfoBar(): InfoBlock {
  const claw = getUiTexture('clawEmpty')
  const reward = getUiTexture('collectReward')
  return levelBarBlock(getUiTexture('collectBg'), 884, 100, [
    { texture: getUiTexture('levelBadge1'), ax: -364, ay: 0.7, width: 120, height: 44 },
    // 第一個爪槽在 prefab 內就是「已收集」狀態（子節點疊了 pic_claw_up）。
    { texture: claw, ax: -255.9, ay: 0, width: 66, height: 66 },
    { texture: getUiTexture('clawFilled'), ax: -255.9, ay: 0, width: 66, height: 66 },
    { texture: claw, ax: -184.4, ay: 0, width: 66, height: 66 },
    { texture: reward, ax: -99.9, ay: 0, width: 104, height: 56, caption: 'X3', captionFont: 50 },
    { texture: claw, ax: -16.2, ay: 0, width: 66, height: 66 },
    { texture: claw, ax: 54.5, ay: 0, width: 66, height: 66 },
    {
      texture: reward,
      ax: 142.1,
      ay: -15.1,
      width: 104,
      height: 56,
      caption: 'X5',
      captionFont: 50,
      overlay: { texture: getUiTexture('add3Spins'), ax: -6.5, ay: 42.7, width: 138, height: 48 },
    },
    { texture: claw, ax: 224.1, ay: 0, width: 66, height: 66 },
    { texture: claw, ax: 295, ay: 0, width: 66, height: 66 },
    // Level3_Info 沒有底圖（prefab 內該節點的 Image 無 m_Sprite），只有「+8 旋轉」字圖。
    {
      ax: 380.7,
      ay: -15.1,
      width: 104,
      height: 56,
      overlay: { texture: getUiTexture('add8Spins'), ax: 16.3, ay: 17.7, width: 138, height: 48 },
    },
  ])
}

/** 依序回傳 GameRuleInfo 的 24 個段落。 */
export function gameRuleSections(): InfoBlock[] {
  const title = (key: string): InfoBlock => titleBlock(t(key), GAME_TITLE_GRADIENT)
  const wild = (highSymbolId: number): ReturnType<typeof getSymbolTexture> =>
    getWildVariantTexture(highSymbolId, 1) ?? getSymbolTexture(highSymbolId, 1)
  const wolf = (level: number): ReturnType<typeof getSymbolTexture> =>
    getScatterWolfTexture(level, 1) ?? getSymbolTexture(12, 1)

  return [
    title('game.Info_GameRule'),
    bodyBlock(t('game.Info_GameRule_1'), 1208.85),
    payTableBlock(),

    title('game.Info_Symbols'),
    groupBlock(
      [
        imageBlock(getSymbolTexture(0, 1), SYMBOL_W, SYMBOL_ROW_H),
        bodyBlock(t('game.Info_Symbols_1'), 906.3853),
        imageRowBlock(
          [2, 3, 4].map((id) => ({ texture: wild(id), width: SYMBOL_W, height: SYMBOL_ROW_H })),
          SYMBOL_ROW_SPACING,
        ),
        bodyBlock(t('game.Info_Symbols_2'), 906.3853),
        imageRowBlock(
          [
            { texture: getSymbolTexture(12, 1), width: SYMBOL_W, height: SYMBOL_ROW_H },
            ...[1, 2, 3].map((level) => ({ texture: wolf(level), width: SYMBOL_W, height: SYMBOL_ROW_H })),
          ],
          SYMBOL_ROW_SPACING,
        ),
        bodyBlock(t('game.Info_Symbols_3'), 1055.3267),
      ],
      WILD_GROUP_SPACING,
    ),

    // ⚠️ Unity 這個標題**沒有** LocalizeStringEvent，是硬編英文；本移植改走手寫 key（見 messages.ts）
    //    以免非英語系突兀跳出英文。
    titleBlock(t('info.charactersWild'), GAME_TITLE_GRADIENT),
    imageRowBlock(
      [2, 3, 4].map((id) => ({ texture: getSymbolTexture(id, 4), width: SYMBOL_W, height: CHARACTER_H })),
      CHARACTER_ROW_SPACING,
    ),
    bodyBlock(t('game.Info_Symbols_4'), 1167.0605),

    title('game.Info_FreeGame'),
    imageBlock(getUiTexture('picWildInfo'), 664, 498),
    bodyBlock(t('game.Info_FreeGame_1'), 900.1613),
    // 容器 400×280（寬度不影響排版）、內含 280×280 的倍率牌與 fontSize 70 的「X10」疊字。
    captionedImageBlock(getUiTexture('picMultiple'), 280, 280, 280, 'X10', 70, -27.3),
    bodyBlock(t('game.Info_FreeGame_2'), 1116.5415),
    freeSpinInfoBar(),
    bodyBlock(t('game.Info_FreeGame_3'), 928.8021),
    bodyBlock(t('game.Info_FreeGame_4'), 1107.5144),

    title('game.Info_Cascading'),
    bodyBlock(t('game.Info_Cascading_1'), 1107.5144),
    bodyBlock(t('game.Info_Cascading_2'), 1107.5144),

    title('game.Info_BuyFreeSpins'),
    bodyBlock(t('game.Info_BuyFreeSpins_1'), 1107.5144),
    // Unity 節點叫 `Info_BuyFreeSpins_2`，但綁的 key 是 `Info_BuyFreeSpins_3`（字表跳過 _2）。
    bodyBlock(t('game.Info_BuyFreeSpins_3'), 1107.5144),

    title('game.Info_MaxWin'),
    bodyBlock(t('game.Info_MaxWin_1'), 1167.0605),
  ]
}
